import { Alert, Box, CircularProgress, Divider, IconButton, Link, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { formatDate, formatDateTime } from "../../utilities/globalServiceUtils";
import { ASSETS_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import Datagrid from "../Datagrid/Datagrid";
import { useSite } from "../../context/siteContext";
import SummarizeIcon from "@mui/icons-material/Summarize";
import RequestHistoryDetails from "../RequestHistoryDetails/RequestHistoryDetails";
import { ALERT_MESSAGES, TABLE_HEADERS, TITLES } from "../../constants";
import { useTranslation } from "react-i18next";
import { requestListHistory } from "../../services/requestHistoryService";

const RequestsHistory = () => {
	const [requestHistory, setRequestHistory] = useState(null);
	const [selectedRLHistoryRows, setSelectedRLHistoryRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [viewDetails, setViewDetails] = useState(false);
	const [selectedRequest, setSelectedRequest] = useState({});
	const [error, setError] = useState("");
	const { selectedSite, selectedSiteData } = useSite();
	const [filteredHistory, setFilteredHistory] = useState(null); // Add this
	const [searchText, setSearchText] = useState(""); // Add this
	const navigate = useNavigate();
	const { t } = useTranslation();

	useEffect(() => {
		if (selectedSite) {
			fetchHistory();
		}
	}, [selectedSite, selectedSiteData?.site_id, selectedSiteData?.cus_id]);

	const handleRowSelect = (rows) => {
		setSelectedRLHistoryRows(rows);
	};

	useEffect(() => {
		if (!requestHistory) {
			setFilteredHistory(null);
			return;
		}

		if (!searchText) {
			setFilteredHistory(requestHistory);
			return;
		}

		const filtered = requestHistory.filter(row => {
			// Search across all fields
			return Object.entries(row).some(([key, value]) => {
				if (key === 'created' && value) {
					// For date field, search in formatted date
					const formattedDate = formatDate(value);
					return formattedDate.toLowerCase().includes(searchText.toLowerCase());
				}
				// For other fields, search in string value
				return String(value || '').toLowerCase().includes(searchText.toLowerCase());
			});
		});

		setFilteredHistory(filtered);
	}, [requestHistory, searchText]);
    // use global formatters

	const fetchHistory = async () => {
		setLoading(true);
		const req_body = {
			selectedSite,
			selectedSiteId: selectedSiteData?.site_id,
			selectedCusId: selectedSiteData?.cus_id,
		};
		try {
			const response = await requestListHistory(req_body);
			if (response.status) {
				setRequestHistory(response.data.history);
				// Don't set filteredHistory here - let useEffect handle it
				setLoading(false);
			}
		} catch (error) {
			console.log(error);
			setError(error);
		}
	};

	const handleViewDetails = (row) => {
		console.log(row);
		setSelectedRequest(row);
		setViewDetails(true);
	};

	const handleViewDetailsRowClick = (row) => {
		console.log(row);
		setSelectedRequest(row.row);
		setViewDetails(true);
	};

	const columns = [
		{
			field: "request_number",
			headerName: t('TABLE_HEADERS.REQUEST'),
			flex: 1,
			renderCell: (params) => (
				<Tooltip title={t('TITLES.VIEW_REQUEST_DETAILS')}>
					<span
						style={{
							cursor: "pointer",
							fontWeight: "bold",
						}}
						onMouseEnter={(e) => (e.target.style.fontWeight = "bold")}
						onMouseLeave={(e) => (e.target.style.fontWeight = "bold")}>
						{params.value}
					</span>
				</Tooltip>
			),
		},
		{ field: "parts_count", headerName: t('TABLE_HEADERS.REQUEST_PARTS'), width: 100, align: "center", headerAlign: "center" },
		{ field: "user_name", headerName: t('TABLE_HEADERS.REQUESTED_BY'), width: 200, align: "center", headerAlign: "center" },
		{
			field: "created",
			headerName: t('TABLE_HEADERS.CREATED_DATE'),
			valueFormatter: (value) => formatDateTime(value),
			width: 250,
			align: "center",
			headerAlign: "center",
			// Remove getApplyQuickFilterFn - it won't work with custom component
		},
		{
			headerName: t('TABLE_HEADERS.VIEW_DETAILS'),
			width: 100,
			sortable: false,
			headerAlign: "center",
			align: "center",
			renderCell: (params) => (
				<div>
					<Tooltip title={t('TABLE_HEADERS.VIEW_DETAILS')}>
						<IconButton
							color="primary"
							aria-label="View Details"
							onClick={(event) => {
								event.stopPropagation();
								handleViewDetails(params.row);
							}}>
							<SummarizeIcon />
						</IconButton>
					</Tooltip>
				</div>
			),
		},
	]

	if (loading) {
		return (
			<Box
				sx={{
					width: "100%",
					height: "400px",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
				}}>
				<CircularProgress />
			</Box>
		);
	}

	if (error && error !== "" && !loading) {
		return (
			<Alert severity="error" className="error-alert" sx={{ marginTop: "10px" }}>
				{t('ALERT_MESSAGES.ERROR_FETCHING_HISTORY')}
			</Alert>
		);
	}

	if ((!requestHistory || requestHistory.length <= 0) && !loading && error !== "") {
		return (
			<Alert severity="info" className="error-alert" sx={{ marginTop: "10px" }}>
				{this('ALERT_MESSAGES.NO_HISTORY_AVAILABLE')}
			</Alert>
		);
	}

	return (
		<>
			<Datagrid
				key="request_number"
				data={filteredHistory} // Use filtered data instead of requestHistory
				columns={columns}
				pageSize={5}
				showSearchBar={true}
				checkboxSelection={false}
				rowClick={true}
				onRowClick={handleViewDetailsRowClick}
				rowIdField="request_number"
				sortBy={[{ field: "request_created_date", sort: "desc" }]}
				// You might need to pass search handlers if your custom component supports them
				onSearch={setSearchText} // Add this if your Datagrid supports it
				searchValue={searchText} // Add this if your Datagrid supports it
			/>
			{viewDetails && (
				<RequestHistoryDetails
					selectedRow={selectedRequest}
					viewDetails={viewDetails}
					setViewDetails={setViewDetails}
				/>
			)}
		</>
	);
};

export default RequestsHistory;
